<template>
  <tbody class="contractor-edit">
    <tr
      v-for="contractor of currentContractors"
      :key="contractor.id"
    >
      <td class="user-account-input">
        <InputField
          :value="contractor.userAccount"
          :pattern="regex.email"
          :inputTip="errorEmail"
          :required="true"
          @updateValue="
            (value) =>
              setContractorInfo({
                id: contractor.id,
                key: 'userAccount',
                val: value,
              })
          "
        />
      </td>
      <td>
        <div class="name-input">
          <input
            v-for="fullnameObj of getFullnameSorts"
            :key="fullnameObj.key"
            :class="['name-input__part', !contractor.validation[fullnameObj.value] && 'error-highlight']"
            :value="contractor[fullnameObj.value]"
            @input="
              (e) =>
                setContractorInfo({
                  id: contractor.id,
                  key: fullnameObj.value,
                  val: e.target.value,
                })
            "
          >
        </div>
      </td>
      <td>
        <HKLSelect
          :class="['contractor-type__select', !contractor.validation.contractorType && 'error-highlight']"
          :value="contractor.contractorType || undefined"
          :options="contractorTypeHklOptions"
          :showSearch="true"
          :dropdownMatchSelectWidth="false"
          :title="contractor.contractorType"
          placeholder=""
          @change="
            (val) =>
              setContractorInfo({
                id: contractor.id,
                key: 'contractorType',
                val,
              })
          "
        />
      </td>
      <td>
        <HKLSelect
          v-hkldinput
          :class="['company-input', !contractor.validation.companyName && 'error-highlight']"
          :value="contractor.companyName || undefined"
          :options="companyHklOptions"
          :showSearch="true"
          :dropdownMatchSelectWidth="false"
          :singleTaggableInput="true"
          :title="contractor.companyName"
          placeholder=""
          @search="(val) => onCompanySearch(val)"
          @input="(val) => onCompanyInput(val, contractor.id)"
          @change="(val) => onCompanyChange(val, contractor.id)"
        />
      </td>
      <td>
        <input
          :class="['office-tel-input', !contractor.validation.officeTel && 'error-highlight']"
          :value="contractor.officeTel"
          @input="
            (e) =>
              setContractorInfo({
                id: contractor.id,
                key: 'officeTel',
                val: e.target.value,
              })
          "
        >
      </td>
      <td>
        <input
          :class="['mobile-input', !contractor.validation.mobile && 'error-highlight']"
          :value="contractor.mobile"
          @input="
            (e) =>
              setContractorInfo({
                id: contractor.id,
                key: 'mobile',
                val: e.target.value,
              })
          "
        >
      </td>
      <td class="last-login">
        {{ formatDate(contractor.user.lastLoginAt) }}
      </td>
      <td class="last-invite">
        {{ formatDate(contractor.lastInviteDateTime) }}
      </td>
      <td>
        <i
          class="fa fa-times fa-lg delete-icon"
          @click="toggleDeleteModal({ id: contractor.id })"
        />
      </td>
    </tr>
    <Modal ref="deleteModal">
      <div slot="content">
        <p class="modal-pure-text">
          {{ deleteModalContent }}
        </p>
      </div>
      <div slot="btns">
        <button
          class="cancel btn btn--darkblue"
          @click="cancelDelete"
        >
          {{ $t("action.cancel") }}
        </button>
        <button
          class="comfirm btn btn--orange"
          @click="confirmDelete"
        >
          {{ $t("action.delete") }}
        </button>
      </div>
    </Modal>
  </tbody>
</template>

<script>
import { mapMutations } from 'vuex'

import InputField from '@/controls/InputField'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Modal from '@/controls/Modal'

import * as regex from '@/utils/regex'
import dateUtil, { DD_MMM_YY_HH_MM as dateFormat } from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import mutationTypes from '@/store/mutation-types'
import * as companiesApi from '@/api/companies/request'
import { companyType } from '@/utils/locale'
import { getSAWorkTypeListMap } from '@/modules/workPermits/consts'
import formFullnameMixin from '@/pages/formFullnameMixin'

export default {
  components: {
    HKLSelect,
    Modal,
    InputField,
  },
  mixins: [formFullnameMixin],
  data () {
    return {
      contractorTypes: getSAWorkTypeListMap(),
      contractorToDelete: '',
      deleteModalContent: i18n.t(
        'standaloneWorkPermit.management.deleteUserTips',
      ),
      searchCompaniesResult: [],
      companyNameSearchText: '',
      errorEmail: i18n.t('hint.inputTip.email'),
      regex,
    }
  },
  computed: {
    currentContractors () {
      return this.$store.state.tenancyContractors.contractors
    },
    contractorTypeHklOptions () {
      return (this.contractorTypes || []).map((type) => ({ name: type, value: type }))
    },
    companyHklOptions () {
      const duplicatedMap = {}
      const uniqueCompanyNames = (this.searchCompaniesResult || []).reduce((acc, company) => {
        const companyName = this.normalizeCompanyName(company && company.name)
        if (!companyName) return acc
        const normalisedName = this.normalizeDuplicateKey(companyName)
        if (duplicatedMap[normalisedName]) return acc
        duplicatedMap[normalisedName] = true
        acc.push(companyName)
        return acc
      }, [])
      const temporaryCompanyNameOption = this.getTemporaryCompanyNameOption()
      const companyOptions = uniqueCompanyNames.map((name) => ({ name, value: name }))
      return temporaryCompanyNameOption
        ? [temporaryCompanyNameOption, ...companyOptions]
        : companyOptions
    },
  },
  methods: {
    normalizeCompanyName (name) {
      return String(name || '')
        .normalize('NFKC')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    normalizeDuplicateKey (name) {
      return String(name || '')
        .normalize('NFKC')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
    },
    getTemporaryCompanyNameOption () {
      const searchText = String(this.companyNameSearchText || '').trim()
      if (!searchText) return null

      const normalizedSearchText = this.normalizeDuplicateKey(searchText)
      const hasExisting = (this.searchCompaniesResult || []).some((company) =>
        this.normalizeDuplicateKey(company && company.name) === normalizedSearchText,
      )
      if (hasExisting) return null

      return {
        name: searchText,
        value: searchText,
        isSingleTaggableInputTag: true,
        hklSelectHint: i18n.t('hint.addTempCompany'),
      }
    },
    onCompanyChange (val, id) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      if (!name) {
        this.clearCompanySelection(id)
        return
      }
      this.companyNameSearchText = ''
      this.selectCompany(name, id)
    },
    onCompanyInput (val, id) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      if (!name) {
        this.clearCompanySelection(id)
        return
      }
      this.setContractorInfo({
        id,
        key: 'companyName',
        val: name,
      })
      this.setContractorInfo({
        id,
        key: 'companyId',
        val: '',
      })
    },
    clearCompanySelection (id) {
      this.companyNameSearchText = ''
      this.setContractorInfo({
        id,
        key: 'companyName',
        val: '',
      })
      this.setContractorInfo({
        id,
        key: 'companyId',
        val: '',
      })
    },
    ...mapMutations('tenancyContractors', {
      setContractorInfo: mutationTypes.TENANCY_CONTRACTOR_SET_INFO,
      deleteContractor: mutationTypes.TENANCY_CONTRACTOR_DELETE,
    }),
    toggleDeleteModal (id) {
      this.contractorToDelete = id
      this.$refs.deleteModal.open()
    },
    cancelDelete () {
      this.contractorToDelete = ''
      this.$refs.deleteModal.close()
    },
    confirmDelete () {
      this.deleteContractor(this.contractorToDelete)
      this.$refs.deleteModal.close()
    },
    async searchCompanies (query) {
      query.type = companyType.C
      query.active = true
      this.searchCompaniesResult = await companiesApi.searchCompanies(query)
    },
    onCompanySearch (query) {
      this.companyNameSearchText = String(query || '').trim()
      return this.searchCompanies({ name: this.companyNameSearchText })
    },
    selectCompany (val, id) {
      const companyName = this.normalizeCompanyName(val)
      if (!companyName) return
      this.setContractorInfo({
        id,
        key: 'companyName',
        val: companyName,
      })
      const normalizedCompanyName = this.normalizeDuplicateKey(companyName)
      const selectedCompany = this.searchCompaniesResult.find(
        (company) => this.normalizeDuplicateKey(company && company.name) === normalizedCompanyName,
      )
      this.setContractorInfo({
        id,
        key: 'companyId',
        val: selectedCompany ? selectedCompany.id : '',
      })
    },
    formatDate (time) {
      return time ? dateUtil.format(time, dateFormat) : '-'
    },
  },
}
</script>

<style lang="scss" scoped>
@mixin input-field {
  padding: 1px 2px 1px 10px;
  margin: 0 0 5px;
  height: 40px;
  border: solid 1px #B5B5B5;
  font-family: Verdana;
  font-size: 14px;
  color: #0C3A58;
}
.contractor-edit {
  & ::v-deep .contractor-type__select {
    @include input-field;
    padding: 0;

    .ant-select-selection-selected-value {
      position: absolute;
      left: 0;
      right: 0;
      max-width: none;
    }
  }
  & ::v-deep .company-input {
    @include input-field;
    padding: 0;
    .has-data-wrapper {
      min-width: 400px;
    }
  }
}
.user-account-input {
  min-width: 200px;
}
.name-input {
  min-width: 150px;
  display: flex;
}
.name-input__part {
  @include input-field;
  width: 50%;
  max-width: 115px;
}
.office-tel-input {
  @include input-field;
  width: 130px;
}
.mobile-input {
  @include input-field;
  width: 130px;
}
.last-login {
  text-align: center;
}
.last-invite {
  text-align: center;
}
.delete-icon {
  margin-right: 10px;
  cursor: pointer;
}
.error-highlight {
  background-color: #f9dcda;
}
</style>
